import type { Seo } from '@integration-layer/ampliance/schemas/seo-schema.localized'

/**
 * Represents metadata for an editorial page.
 * @typedef {Object} EditorialMeta
 * @property {'editorial'} type - Type of metadata
 * @property {string} title - Page title
 * @property {string} brand - Brand name
 */
export type EditorialMeta = {
  type: 'editorial'
  title: string
  brand: string
}

/**
 * Represents metadata for a Product Listing Page (PLP).
 * @typedef {Object} PlpMeta
 * @property {'plp'} type - Type of metadata
 * @property {string} title - Page title
 */
export type PlpMeta = {
  type: 'plp'
  title: string
}

/**
 * Represents metadata for a Product Detail Page (PDP).
 * @typedef {Object} PdpMeta
 * @property {'pdp'} type - Type of metadata
 * @property {string} product - Product identifier
 * @property {string} collectionName - Name of the product collection
 * @property {string} gender - Gender category
 */
export type PdpMeta = {
  type: 'pdp'
  product: string
  collectionName: string
  gender: string
}

/**
 * Represents metadata for a brand homepage.
 * @typedef {Object} BrandHomepageMeta
 * @property {'brandHomepage'} type - Type of metadata
 * @property {string} slug - URL slug for the brand homepage
 * @property {string} brand - Brand name
 */
export type BrandHomepageMeta = {
  type: 'brandHomepage'
  slug: string
  brand: string
}

/**
 * Union type for extra metadata types.
 * @typedef {EditorialMeta | PlpMeta | PdpMeta | BrandHomepageMeta} ExtraData
 */
export type ExtraData = EditorialMeta | PlpMeta | PdpMeta | BrandHomepageMeta

/**
 * Composable for managing SEO metadata from Amplience.
 * Generates and sets page metadata based on Amplience content and extra data.
 *
 * @param {Object} props - Configuration options for SEO metadata
 * @param {string} [props.deliveryId] - Amplience delivery ID
 * @param {string} [props.locale] - Content locale
 * @param {string} [props.vse] - Amplience VSE (Virtual Staging Environment)
 * @param {Seo} [props.content] - SEO content from Amplience
 * @param {ExtraData} [props.extraData] - Additional metadata
 *
 * @returns {Promise<void>}
 *
 * @example
 * await useAmplienceSeo({
 *   deliveryId: 'abc123',
 *   extraData: {
 *     type: 'pdp',
 *     product: 'product-123',
 *     collectionName: 'Summer Collection',
 *     gender: 'unisex'
 *   }
 * });
 */
export const useAmplienceSeo = async (props: {
  deliveryId?: string
  locale?: string
  vse?: string
  content?: Seo
  extraData?: ExtraData
}) => {
  const { getMetadata } = useSeo()
  const nuxtApp = useNuxtApp()
  const data = props.deliveryId ? await useComponentResponse<Seo>(props) : null

  /**
   * Computed metadata combining Amplience data and extra metadata.
   * Provides fallback values if no metadata is available.
   *
   * @type {ComputedRef<{title: string, description: string}>}
   */
  const metaData = computed(() => {
    // Initialize meta data from 'data', falling back to empty strings if not available
    const metaFromData = {
      title: data?.value?.title ?? '',
      description: data?.value?.description ?? '',
    }

    // If extraData exists, merge its metadata where data is missing
    const metaFromExtra = props.extraData ? getMetadata(props.extraData) : null

    return {
      title: metaFromData.title || metaFromExtra?.title || 'Armani',
      description:
        metaFromData.description || metaFromExtra?.description || 'Armani',
    }
  })

  /**
   * Generates robot meta tags from Amplience data.
   *
   * @type {Array<{name: string, content: string}>}
   */
  const metaRobots =
    data?.value?.robotsMetaTags?.map(content => ({
      name: 'robots',
      content,
    })) || []

  // Set page metadata using Nuxt's useHead
  nuxtApp.runWithContext(() => {
    useHead({
      title: metaData.value.title,
      meta: [
        { name: 'description', content: metaData.value?.description },
        ...metaRobots,
      ],
    })
  })
}
